import { CheckCircleIcon } from '@chakra-ui/icons';
import { Text, Flex, Icon, HStack, useColorModeValue } from '@chakra-ui/react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { countriesInfo, Country, type CountryCode } from '@/models/country';
import { Flag } from '../Flag';

interface Config {
  default: string;
  selected: string;
}

interface TeamSelectProps {
  countryCode: Country;
  selected?: boolean;
  nonSelectable?: boolean;
  /** Smaller flag, text, and padding for dense grids (e.g. home page). */
  compact?: boolean;
  onClick?: (countryCode: CountryCode) => void;
  config?: {
    borderColor?: Config;
    iconColor?: Config;
    iconBorderColor?: Config;
    bgColor?: Config;
    textColor?: Config;
  };
}

export const TeamSelect: React.FC<TeamSelectProps> = ({
  countryCode,
  selected,
  nonSelectable,
  compact,
  onClick,
  config,
}) => {
  const countryName = countriesInfo[countryCode].name;

  const [isSelected, setIsSelected] = useState<boolean>(false);
  useEffect(() => {
    setIsSelected(selected ?? false);
  }, [selected]);

  const handleSelect = () => {
    if (nonSelectable) return;
    onClick?.(countryCode) ?? setIsSelected(!isSelected);
  };

  const selectedBg = useColorModeValue('gray.50', 'gray.600');
  const defaultBorder = useColorModeValue('gray.200', 'whiteAlpha.300');
  const defaultText = useColorModeValue('gray.600', 'gray.200');

  config = {
    textColor: {
      default: defaultText,
      selected: defaultText,
    },
    bgColor: {
      default: 'transparent',
      selected: selectedBg,
    },
    borderColor: {
      default: defaultBorder,
      selected: '#00B93C',
    },
    iconColor: {
      default: 'transparent',
      selected: '#00B93C',
    },
    iconBorderColor: {
      default: 'gray.500',
      selected: 'transparent',
    },
    ...config,
  };

  const { textColor, bgColor, iconColor, iconBorderColor, borderColor } = config;

  const bg = isSelected ? bgColor?.selected : bgColor?.default;
  const borderColour = isSelected ? borderColor?.selected : borderColor?.default;
  const iconColour = isSelected ? iconColor?.selected : iconColor?.default;
  const iconBorderColour = isSelected ? iconBorderColor?.selected : iconBorderColor?.default;
  const iconBorderWidth = isSelected ? 0 : '2px';
  const textColour = isSelected ? textColor?.selected : textColor?.default;

  return (
    <Flex
      bg={bg}
      justify='space-between'
      alignItems='center'
      borderRadius={compact ? '6px' : { base: '8px', md: '12px', lg: '16px' }}
      borderColor={borderColour}
      borderWidth={compact ? '1px' : '2px'}
      onClick={handleSelect}
      minW={compact ? 0 : { base: '100px', md: '200px' }}
      width='full'
      px={compact ? '6px' : '9px'}
      py={compact ? '4px' : '6px'}
      cursor={nonSelectable ? 'default' : 'pointer'}
    >
      <HStack spacing={compact ? 2 : 3} minW={0}>
        <Flag
          country={countryCode}
          w={compact ? { base: '22px', md: '26px' } : { base: '48px', md: '72px' }}
          borderWidth={compact ? '1px' : '2px'}
          borderColor='gray.100'
          borderRadius={compact ? '4px' : '8px'}
          boxShadow={compact ? 'none' : 'sm'}
          flexShrink={0}
        />
        <Text
          fontSize={compact ? { base: '10px', md: '11px' } : 'small'}
          color={textColour}
          noOfLines={2}
          lineHeight='1.2'
        >
          {countryName}
        </Text>
      </HStack>
      {isSelected && (
        <Icon
          as={CheckCircleIcon}
          color={iconColour}
          borderRadius='full'
          borderWidth={iconBorderWidth}
          borderColor={iconBorderColour}
          boxSize={compact ? '14px' : '18px'}
          flexShrink={0}
        />
      )}
    </Flex>
  );
};

export default TeamSelect;
